import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "../lib/adminApi";
import { Field, TextInput, NumberInput, TextArea, Toggle, StringListEditor, PageHeader, SaveBar, Tabs, Checkbox, Select, ColorInput, JsonEditor, isValidHex, isValidUrl } from "./formKit";
import { Plus, ChevronUp, ChevronDown, Trash2, Pencil, ArrowLeft } from "lucide-react";

/**
 * Generic CRUD page for collection-type resources.
 * Renders a list with reorder/toggle/delete and an inline editor when an item is selected/new.
 */
export default function ResourceCrudPage({
  resource,            // e.g. "programs"
  title,
  subtitle,
  blank,               // function returning a blank item
  renderRow,           // function(item) -> ReactNode (compact row preview)
  renderForm,          // function({ form, setField, errors }) -> ReactNode (full form fields)
  validate,            // function(form) -> { ok: bool, message?: string }
  testIdPrefix,
}) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | item.id
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({}); // key -> error message

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/${resource}`);
      setItems(data.items || []);
    } catch {
      toast.error(`Couldn't load ${resource}.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  const startNew = () => {
    setEditing("new");
    setForm(blank());
    setFormErrors({});
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setForm({ ...item });
    setFormErrors({});
  };

  const cancel = () => {
    setEditing(null);
    setForm(null);
    setFormErrors({});
  };

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setError = (k, msg) =>
    setFormErrors((e) => ({ ...e, [k]: msg || undefined }));

  const save = async () => {
    // Block save if any per-field errors exist
    const activeErrors = Object.entries(formErrors).filter(([, v]) => !!v);
    if (activeErrors.length > 0) {
      toast.error(`Please fix: ${activeErrors.map(([k]) => k).join(", ")}`);
      return;
    }
    if (validate) {
      const r = validate(form);
      if (!r.ok) {
        toast.error(r.message || "Please fix the form.");
        return;
      }
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await adminApi.post(`/admin/${resource}`, form);
        toast.success("Created.");
      } else {
        await adminApi.put(`/admin/${resource}/${editing}`, form);
        toast.success("Saved.");
      }
      await refresh();
      cancel();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't save.";
      toast.error(typeof detail === "string" ? detail : "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item permanently?")) return;
    try {
      await adminApi.delete(`/admin/${resource}/${id}`);
      toast.success("Deleted.");
      await refresh();
    } catch {
      toast.error("Couldn't delete.");
    }
  };

  const toggleActive = async (item) => {
    try {
      await adminApi.put(`/admin/${resource}/${item.id}`, {
        ...item,
        active: !item.active,
      });
      await refresh();
    } catch {
      toast.error("Couldn't update.");
    }
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    try {
      await adminApi.post(`/admin/${resource}/reorder`, { ids: next.map((i) => i.id) });
      await refresh();
    } catch {
      toast.error("Couldn't reorder.");
    }
  };

  if (editing) {
    return (
      <div data-testid={`${testIdPrefix}-editor`}>
        <button
          onClick={cancel}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 mb-4"
          data-testid={`${testIdPrefix}-back`}
        >
          <ArrowLeft className="h-4 w-4" /> Back to list
        </button>
        <PageHeader
          title={editing === "new" ? `New ${title.toLowerCase()}` : `Edit ${title.toLowerCase()}`}
          subtitle="Fields marked optional can be left blank — defaults will apply."
        />
        <div className="rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 grid gap-5">
          {renderForm({ form, setField, errors: formErrors, setError })}
        </div>
        <SaveBar
          onSave={save}
          saving={saving}
          disabled={Object.values(formErrors).some(Boolean)}
        />
      </div>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-list`}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <button
            onClick={startNew}
            className="btn-primary"
            data-testid={`${testIdPrefix}-new-btn`}
          >
            <Plus className="h-4 w-4" />
            Add new
          </button>
        }
      />

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl bg-white border border-dashed border-slate-300 p-10 text-center">
          <p className="text-slate-500 text-sm">No items yet.</p>
          <button onClick={startNew} className="btn-primary mt-4">
            <Plus className="h-4 w-4" /> Add the first one
          </button>
        </div>
      ) : (
        <ul className="grid gap-3">
          {items.map((item, idx) => (
            <li
              key={item.id}
              className={`rounded-2xl bg-white border ${
                item.active ? "border-slate-200" : "border-slate-200 opacity-70"
              } p-4 sm:p-5 grid sm:grid-cols-[auto_1fr_auto] gap-4 items-center`}
              data-testid={`${testIdPrefix}-row-${item.id}`}
            >
              <div className="flex sm:flex-col gap-1">
                <button
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 text-slate-500 disabled:opacity-30 hover:bg-slate-50"
                  data-testid={`${testIdPrefix}-move-up-${item.id}`}
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  onClick={() => move(idx, 1)}
                  disabled={idx === items.length - 1}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 text-slate-500 disabled:opacity-30 hover:bg-slate-50"
                  data-testid={`${testIdPrefix}-move-down-${item.id}`}
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
              <div className="min-w-0">{renderRow(item)}</div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Toggle
                  value={item.active}
                  onChange={() => toggleActive(item)}
                />
                <button
                  onClick={() => startEdit(item)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3 py-1.5 text-xs font-bold"
                  data-testid={`${testIdPrefix}-edit-${item.id}`}
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  onClick={() => remove(item.id)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 text-slate-700 px-3 py-1.5 text-xs font-bold hover:bg-red-50 hover:border-red-200 hover:text-red-700"
                  data-testid={`${testIdPrefix}-delete-${item.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Re-export form primitives so resource pages can import from one place
export { Field, TextInput, NumberInput, TextArea, Toggle, StringListEditor, Tabs, Checkbox, Select, ColorInput, JsonEditor, isValidHex, isValidUrl };
